import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import useFiles, { useCurrentKey } from "files/hooks/useFiles";
import FileType from "files/types/enums/fileType";
import { Alert, AlertDescription } from "~/components/ui/alert";
import { Button } from "~/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "~/components/ui/dialog";
import { BaseInput } from "~/components/ui/input";
import useToast from "~/components/ui/toast";
import rumClient from "~/utils/monitoring";

const { VITE_RWB_API } = import.meta.env;

interface CreateFolderForm {
  folderName: string;
}

const ModalBody = ({ onClose, prefix }: { onClose: () => void; prefix?: string }) => {
  const { data: filesData, mutate: mutateFiles } = useFiles(prefix);
  const { showSuccess, showError } = useToast();
  const [error, setError] = useState(false);
  const routeKey = useCurrentKey();
  // Prefer an explicit prefix (routes that useBasePath doesn't recognize, e.g. nextgen) and
  // fall back to deriving the current directory from the route (classic / workbench).
  const currentKey = prefix ?? routeKey;
  const form = useForm<CreateFolderForm>({
    mode: "onChange",
    defaultValues: { folderName: "" },
  });

  const onSubmit = async (data: CreateFolderForm) => {
    setError(false);

    try {
      const folderPath = `${currentKey}${data.folderName}`;

      const response = await fetch(`${VITE_RWB_API}/file-browser/create-folder`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          folder_path: folderPath,
        }),
      });

      if (!response.ok) {
        throw new Error(`Unable to create folder: ${response.status}`);
      }

      await mutateFiles();
      showSuccess(`Created folder "${data.folderName}"`);
      onClose();
    } catch (e) {
      setError(true);
      showError("Unable to create folder");
      console.error(e);
      rumClient?.recordError(e);
    }
  };

  return (
    <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col gap-4">
      <DialogHeader>
        <DialogTitle>New Folder</DialogTitle>
        <DialogDescription className="sr-only">
          Create a new folder in the current directory
        </DialogDescription>
      </DialogHeader>
      {error && (
        <Alert variant="destructive">
          <AlertDescription>Unable to create folder</AlertDescription>
        </Alert>
      )}
      <Controller
        control={form.control}
        name="folderName"
        rules={{
          required: { value: true, message: "Required" },
          validate: {
            validName: (value: string) => {
              if (value.includes("/")) {
                return 'Folder name cannot contain "/"';
              }
            },
            uniqueName: (value: string) => {
              // Get keys of existing subdirectories
              const siblings = filesData?.children
                ?.filter((file) => file.type === FileType.DIRECTORY)
                .map((file) => file.key);

              const objectKey = `${filesData?.current_file?.key}${value}/`;

              if (siblings?.includes(objectKey)) {
                return "Folder already exists";
              }
            },
          },
        }}
        render={({ field, fieldState }) => (
          <BaseInput
            {...field}
            label="Folder name *"
            autoFocus
            autoComplete="off"
            disabled={form.formState.isSubmitting}
            errorMessage={fieldState.error?.message}
            aria-invalid={!!fieldState.error}
          />
        )}
      />
      <DialogFooter>
        <Button
          type="button"
          variant="outline"
          onClick={onClose}
          disabled={form.formState.isSubmitting}
        >
          Cancel
        </Button>
        <Button
          type="submit"
          disabled={!form.formState.isValid}
          loading={form.formState.isSubmitting}
        >
          Create
        </Button>
      </DialogFooter>
    </form>
  );
};

interface CreateFolderDialogProps {
  open: boolean;
  onClose: () => void;
  prefix?: string;
}

/**
 * Shadcn rebuild of `files/components/CreateFolderDialog`, shared by the
 * nextgen embedded project Files tab and the personal dataset Files tab.
 * Same validation and create-folder request as the original; only the
 * dialog/input chrome changed.
 */
export const CreateFolderDialog = ({ open, onClose, prefix }: CreateFolderDialogProps) => (
  <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
    <DialogContent>
      <ModalBody onClose={onClose} prefix={prefix} />
    </DialogContent>
  </Dialog>
);
